import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setSessionFacility } from "@/lib/api";
import { localeFor } from "@/lib/money";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // null = checking, false = anonymous, true = authenticated
  const [status, setStatus] = useState(null);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setStatus(true);
    } catch {
      setUser(null);
      setStatus(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  // Re-check the session (and therefore facility access) whenever the tab regains
  // focus. This means when a Global Admin attaches the user to a new gym in another
  // tab/device, the user's facility switcher and role update within seconds of them
  // switching back to their app — no manual F5 needed.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        check();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [check]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* ignore */
    }
    setUser(null);
    setStatus(false);
  };

  const switchFacility = useCallback(async (facilityId) => {
    await setSessionFacility(facilityId);
    // Hard-navigate to the Dashboard so the user lands on the overview of the new facility.
    window.location.href = "/";
  }, []);

  // Currency display:
  //   1. Use the facility's explicit `currency_symbol` if the admin set one (e.g. "₹", "රු", "$").
  //   2. If they cleared it, fall back to "LKR " / "USD " / "AED " etc. so the UI still labels the money.
  //   3. Only when no facility is loaded at all (e.g. pre-login), use the historic ₹ default.
  const rawSymbol = user?.active_facility?.currency_symbol;
  const currencyCode = user?.active_facility?.currency_code || "INR";
  const currency = (rawSymbol != null && String(rawSymbol).trim())
    ? String(rawSymbol)
    : (user?.active_facility ? `${currencyCode} ` : "₹");
  // Number grouping for every formatter in the app (lib/money.js numberLocale), set before
  // the children render so the first paint already uses it.
  window.__NUMBER_LOCALE__ = localeFor(currencyCode);

  return (
    <AuthContext.Provider value={{
      user, setUser, status, setStatus,
      refresh: check, logout, switchFacility,
      activeFacility: user?.active_facility || null,
      facilities: user?.facilities || [],
      effectiveRole: user?.effective_role || user?.role,
      viewOnly: !!user?.view_only,
      currency, currencyCode,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
